import { useCallback, useEffect, useRef, useState } from 'react';

export const NEXT = 'next';
export const PREV = 'prev';

export type ScrollPosition = {
  start: boolean;
  end: boolean;
};

function scrollThumbnailIntoView(id: string) {
  const el = document.getElementById(id);
  el?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  });
}

export const useActivityPreview = ({
  initialIndex,
}: {
  initialIndex: number;
}) => {
  const thumbnailScrollRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [index, setIndex] = useState<number>(initialIndex);

  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    start: true,
    end: false,
  });

  const handleMoveThumbnails = useCallback(
    (action: typeof NEXT | typeof PREV) => {
      if (thumbnailScrollRef.current) {
        const width =
          thumbnailScrollRef.current.parentElement?.offsetWidth || 0;
        thumbnailScrollRef.current.scrollBy({
          behavior: 'smooth',
          left: action === NEXT ? width : -width,
        });
      }
    },
    []
  );

  const handleOnScroll = useCallback(() => {
    if (thumbnailScrollRef.current) {
      setScrollPosition({
        ...scrollPosition,
        start: thumbnailScrollRef.current.scrollLeft === 0,
        end:
          thumbnailScrollRef.current.scrollLeft +
            thumbnailScrollRef.current.clientWidth >=
          thumbnailScrollRef.current.scrollWidth,
      });
    }
  }, [scrollPosition]);

  const handleThumbnailSelect = useCallback(
    (index: number) => {
      setIsLoading(true);
      setIndex(index);
      scrollThumbnailIntoView(`activity-${index}`);
    },
    [setIndex, setIsLoading]
  );

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [isLoading]);

  return {
    index,
    isLoading,
    thumbnailScrollRef,
    scrollPosition,
    setScrollPosition,
    handleMoveThumbnails,
    handleThumbnailSelect,
    handleOnScroll,
  };
};
